import type { EducationLevel, Gender, Interest, Profession, Profile } from 'models';
import { Col, Form, FormActions, Modal, Row, Title, generateUploadFile, getBirthDateTypeValue } from 'ui';
import { AboutFieldset, type AboutFormValues } from './about-fieldset';
import { AddressFieldset, type AddressFormValues } from './address-fieldset';
import { NameFieldset, type NameFormValues } from './name-fieldset';
import { OtherDetailsFieldset, type OtherDetailsFormValues } from './other-details-fieldset';
import { ProgressBar } from './progress-bar';

export type ProfileFormValues = AboutFormValues & AddressFormValues & NameFormValues & OtherDetailsFormValues;

const getInitialValues = (user: Profile): Partial<ProfileFormValues> => ({
  apt: user.apt,
  biography: user.biography,
  birthdate: getBirthDateTypeValue(user.birthdate),
  city: user.city,
  company: user.company,
  country: user.country,
  coverPhoto: user.coverPhoto ? generateUploadFile({ response: user.coverPhoto }) : undefined,
  educationLevel: user.educationLevel?.id,
  email: user.email,
  firstName: user.firstName,
  gender: user.gender?.id,
  interests: user.interests?.map((interest) => interest.id),
  lastName: user.lastName,
  middleName: user.middleName,
  phone: user.phone,
  profession: user.profession?.id,
  profilePhoto: user.profilePhoto ? generateUploadFile({ response: user.profilePhoto }) : undefined,
  state: user.state,
  street: user.street,
  timeZone: user.timeZone,
  zip: user.zip,
});

type P = {
  educationLevels: EducationLevel[];
  genders: Gender[];
  interests: Interest[];
  loading: boolean;
  onCancel: () => void;
  onSubmit: (values: ProfileFormValues) => void;
  professions: Profession[];
  user: Profile;
};

const ProfileForm = ({ educationLevels, genders, interests, loading, onCancel, onSubmit, professions, user }: P) => {
  const [form] = Form.useForm<ProfileFormValues>();
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  const watchedValues = Form.useWatch((allValues) => allValues, form) || {};

  const onFinish = (data: ProfileFormValues) => {
    onSubmit(data);
  };

  return (
    <Form<ProfileFormValues> form={form} initialValues={getInitialValues(user)} onFinish={onFinish}>
      <Row gutter={[0, 'lg']}>
        <Col span={24}>
          <Row justify="space-between">
            <Col>
              <Title level={3} marginBottom>
                About
              </Title>
            </Col>
            <Col span={8}>
              <ProgressBar values={watchedValues} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col span={24}>
              <AboutFieldset<ProfileFormValues> genders={genders}>
                <NameFieldset<ProfileFormValues> />
              </AboutFieldset>
            </Col>
            <Col span={24}>
              <Title level={3} marginBottom>
                Home Address
              </Title>
            </Col>
            <Col span={24}>
              <AddressFieldset<ProfileFormValues> />
            </Col>
            <Col span={24}>
              <Title level={3} marginBottom>
                Other Details
              </Title>
            </Col>
            <Col span={24}>
              <OtherDetailsFieldset<ProfileFormValues>
                educationLevels={educationLevels}
                interests={interests}
                professions={professions}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Modal.ScrollableContent.Footer>
        <FormActions right={{ children: 'Cancel', onClick: onCancel }} submit={{ children: 'Save', loading }} />
      </Modal.ScrollableContent.Footer>
    </Form>
  );
};

export default ProfileForm;
